/**
 * useCollabPages (RT-8) — bidirectional sync between a multi-canvas tab set and
 * a room's `pages` Y.Map (whole-DiagramData LWW per page).
 *
 * room -> tabs: observe the pages map; upsert a tab per page (keyed by pageId),
 *               remove tabs whose page disappeared.
 * tabs -> room: the editor calls the returned `flushTab(tab)` whenever a tab's
 *               content changes; a value just applied from the room is skipped
 *               via a per-page content hash so it isn't written straight back.
 *
 * This sits ABOVE the per-tab editor (one provider for the whole multi-canvas),
 * so each inner editor renders purely from its tab `data` — the normal, tested
 * parse path — and no inner editor needs the `collab` prop.
 */

import { useEffect, useRef, useCallback } from 'react'
import * as Y from 'yjs'
import type { CanvasTab } from '../types/multiCanvas'
import type { DiagramData } from '../types/schema'
import { createYWebsocketSyncProvider, type SyncProvider } from './SyncProvider'
import { getRoomPagesMap, getRoomNodesMap, roomToDiagramData } from './roomBridge'
import type { RoomPageEntry } from './roomContract'

export interface CollabPagesController {
  tabs: CanvasTab[]
  /** Create-or-update a tab keyed by the room pageId. */
  upsertRoomPage: (id: string, name: string, data: DiagramData, order: number) => void
  /** Remove tabs whose pages no longer exist in the room. */
  removeRoomPages: (ids: string[]) => void
}

const hash = (name: string, order: number, data: DiagramData) =>
  `${name} ${order} ${JSON.stringify(data)}`

export function useCollabPages(
  collab: { url: string; room: string } | undefined,
  controller: CollabPagesController,
): { flushTab: (tab: CanvasTab) => void } {
  const ctrlRef = useRef(controller)
  ctrlRef.current = controller

  const providerRef = useRef<SyncProvider | null>(null)
  const pagesMapRef = useRef<Y.Map<unknown> | null>(null)
  const localOriginRef = useRef<string>('')
  const lastSyncedRef = useRef<Map<string, string>>(new Map())

  useEffect(() => {
    if (!collab) return
    const provider = createYWebsocketSyncProvider({ url: collab.url, room: collab.room })
    const doc = provider.doc
    const localOrigin = 'pages:' + doc.clientID
    const pagesMap = getRoomPagesMap(doc)
    const lastSynced = lastSyncedRef.current
    lastSynced.clear()

    providerRef.current = provider
    pagesMapRef.current = pagesMap
    localOriginRef.current = localOrigin

    const applyRoomToTabs = () => {
      const seen = new Set<string>()
      const entries = [...pagesMap.entries()] as [string, RoomPageEntry][]
      entries.sort((a, b) => (a[1]?.order ?? 0) - (b[1]?.order ?? 0))
      for (const [id, entry] of entries) {
        if (!entry || typeof entry !== 'object') continue
        const data = entry.data as DiagramData
        const name = entry.name ?? 'Page'
        const order = entry.order ?? 0
        seen.add(id)
        lastSynced.set(id, hash(name, order, data))
        ctrlRef.current.upsertRoomPage(id, name, data, order)
      }
      const stale = [...lastSynced.keys()].filter((id) => !seen.has(id))
      if (stale.length) {
        for (const id of stale) lastSynced.delete(id)
        ctrlRef.current.removeRoomPages(stale)
      }
    }

    const observer = (_e: unknown, txn: { origin: unknown }) => {
      if (txn.origin === localOrigin) return
      applyRoomToTabs()
    }
    pagesMap.observe(observer as never)
    provider.connect()

    // Seed the room from local tabs if empty (the observer then streams in any
    // pages a peer/MCP adds). Legacy single-canvas rooms (content in the
    // top-level maps) are migrated to a "Main" page once their content has
    // synced — gated on onSync so we don't read an empty pre-sync doc.
    const seedLocalTabs = () => {
      if (pagesMap.size > 0) { applyRoomToTabs(); return }
      doc.transact(() => {
        ctrlRef.current.tabs.forEach((t, i) => {
          // Don't seed empty default tabs ("Untitled 1") into the room — they
          // accumulate as duplicate blank pages across reloads. Only real
          // content seeds; an empty tab stays local until it's edited.
          if (!t.data?.nodes?.length) return
          const entry: RoomPageEntry = { name: t.name, order: t.order ?? i, data: t.data }
          lastSynced.set(t.id, hash(entry.name, entry.order, t.data))
          pagesMap.set(t.id, entry)
        })
      }, localOrigin)
      applyRoomToTabs()
    }

    // CRITICAL: do NOT seed before the initial sync. Pre-sync `pagesMap` is empty
    // even when the room is full, so seeding here would push local/stale tabs into
    // the room and clobber real pages. Everything that writes to the room is gated
    // on onSync, where `pagesMap` reflects the actual synced state.
    provider.onSync(() => {
      // Legacy single-canvas migration (top-level content -> page-main).
      if (getRoomNodesMap(doc).size > 0 && !pagesMap.has('page-main')) {
        const legacy = roomToDiagramData(doc) as unknown as DiagramData
        if (legacy.nodes?.length) {
          lastSynced.set('page-main', hash('Main', -1, legacy))
          doc.transact(() => {
            pagesMap.set('page-main', { name: 'Main', order: -1, data: legacy } as RoomPageEntry)
          }, localOrigin)
        }
      }
      // Seed local tabs ONLY if the synced room is genuinely empty; otherwise this
      // just loads the room's pages into tabs (applyRoomToTabs).
      seedLocalTabs()
    })

    return () => {
      pagesMap.unobserve(observer as never)
      provider.disconnect()
      providerRef.current = null
      pagesMapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab?.url, collab?.room])

  const flushTab = useCallback((tab: CanvasTab) => {
    const pagesMap = pagesMapRef.current
    const provider = providerRef.current
    if (!pagesMap || !provider) return
    // Never push an empty tab to the room (avoids blank "Untitled" duplicates);
    // it syncs once it has content. But DO update a page that already exists.
    if (!tab.data?.nodes?.length && !pagesMap.has(tab.id)) return
    const order = tab.order ?? 0
    const h = hash(tab.name, order, tab.data)
    if (lastSyncedRef.current.get(tab.id) === h) return // unchanged / just applied from room
    lastSyncedRef.current.set(tab.id, h)
    const entry: RoomPageEntry = { name: tab.name, order, data: tab.data }
    provider.doc.transact(() => pagesMap.set(tab.id, entry), localOriginRef.current)
  }, [])

  return { flushTab }
}
